'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { PlaceHolderImages } from '@/lib/placeholder-images';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Linkedin, MapPin } from 'lucide-react';

const titles = [
  'Software Engineer',
  'Unity Developer',
  'Mobile Developer',
  'Game Developer',
  'AI-powered Developer',
];

const profileImage = PlaceHolderImages.find(img => img.id === 'hero-profile');

export default function HeroSection({ id }: { id: string }) {
  const [currentTitleIndex, setCurrentTitleIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsFading(true);
      setTimeout(() => {
        setCurrentTitleIndex((prevIndex) => (prevIndex + 1) % titles.length);
        setIsFading(false);
      }, 500); // Corresponds to fade-out animation duration
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section id={id} className="relative flex items-center justify-center min-h-screen pt-16">
      <div className="container z-10 px-4 mx-auto sm:px-6 lg:px-8">
        <div className="flex flex-col items-center gap-8 text-center md:flex-row md:text-left" style={{ maxWidth: 1200, alignSelf: "center", paddingLeft: "10%" }}>
          {profileImage && (
             <Image
              src={profileImage.imageUrl}
              alt="Marcos Vázquez Rey"
              width={200}
              height={200}
              className="object-cover border-4 rounded-full shadow-lg border-primary/50"
              priority
              data-ai-hint={profileImage.imageHint}
            />
          )}
          <div className="flex-1">
            <h1 className="text-5xl font-bold tracking-tight font-headline text-primary sm:text-6xl md:text-7xl">
              Marcos Vázquez Rey
            </h1>
            <div className="h-12 mt-4 text-2xl font-semibold text-accent sm:text-3xl">
              <span className={cn('transition-opacity duration-500', isFading ? 'opacity-0' : 'opacity-100')}>
                {titles[currentTitleIndex]}
              </span>
            </div>
            <p className="max-w-3xl mx-auto mt-6 text-lg leading-8 md:mx-0 text-foreground/80">
              Hi! I&apos;m a software engineer with over 10 years of experience, specialized in software development with AI, games and mobile app development. Take a look at my previous work right below these lines.
            </p>
            <div className="flex flex-wrap items-center justify-center mt-8 space-x-4 md:justify-start">
              <Button asChild variant="ghost" size="icon">
                <a href="https://www.linkedin.com/in/mvrey/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <Linkedin className="w-6 h-6 text-foreground/80 hover:text-primary" />
                </a>
              </Button>
              <Button asChild variant="ghost" size="icon">
                <a href="https://x.com/m_v_rey" target="_blank" rel="noopener noreferrer" aria-label="X">
                  <svg className="w-6 h-6 text-foreground/80 hover:text-primary" fill="currentColor" viewBox="0 0 300 300" aria-hidden="true">
                    <path d="M178.57 127.15 290.27 0h-26.46l-97.03 110.38L89.34 0H0l117.13 166.93L0 300.25h26.46l102.4-116.59 81.8 116.59h89.34L178.57 127.15zm-20.75 24.23-6.47-9.22L34.43 2.18h43.43l82.4 93.57 6.47 9.22 124.8 178.43h-43.43L157.82 151.38z" />
                  </svg>
                </a>
              </Button>
               <Button asChild variant="link" className="text-foreground/80 hover:text-primary hover:no-underline">
                <a href="https://www.google.com/maps/place/C%C3%A1diz,+Spain" target="_blank" rel="noopener noreferrer" aria-label="Location">
                  <MapPin className="w-5 h-5 mr-2" />
                  Cádiz, Spain
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
